/**
 * Exporta um conjunto de seções visuais como UMA imagem única
 * (PNG ou JPG), combinando os canvas verticalmente com padding.
 */

import {
  captureWithExpand,
  combineCanvasesVertical,
  motivoImagemInviavel,
} from "./captureWithExpand";
import { slugifyFilename, timestampForFilename } from "./formatters";
import { marcaDaExportacao } from "./temaExport";

function downloadDataUrl(dataUrl, filename) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => a.remove(), 0);
}

function canvasToBlob(canvas, mime, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("toBlob retornou null"))),
      mime,
      quality
    );
  });
}

/**
 * Captura visual interna: produz o canvas combinado com header.
 * Reutilizado por `salvarImagem` (com download) e `gerarBlobImagem`
 * (sem download).
 */
async function capturarCanvasCombinado(opts) {
  const {
    titulo = "Exportação",
    elements = [],
    expandHooks = {},
    padding = 40,
    // Tema atual do usuário (light/dark). Quando ausente, cada util cai no
    // default claro — preserva o comportamento do agendador.
    tema = {},
    // Escala do html2canvas (default 2 em captureWithExpand). Listas longas
    // pedem 1 para caber no limite de altura do canvas.
    scale,
  } = opts;

  if (elements.length === 0) {
    throw new Error("capturarCanvasCombinado: forneca pelo menos um elemento.");
  }

  // Larguras ANTES de qualquer regra de captura: depois do export-mode ligado
  // o layout ja refluiu mais largo e a medida nao serve mais (ver
  // captureWithExpand). E o que mantem a imagem com as mesmas colunas da tela.
  const larguraNaTela = new Map();
  elements.forEach((el) => {
    if (el) larguraNaTela.set(el, Math.round(el.getBoundingClientRect().width));
  });

  if (expandHooks.beforeCapture) await expandHooks.beforeCapture();
  const previousBodyAttr = document.body.getAttribute("data-export-mode");
  document.body.setAttribute("data-export-mode", "true");
  await new Promise((resolve) => setTimeout(resolve, 160));
  try {
    const canvases = [];
    for (const el of elements) {
      if (!el) continue;
      // backgroundColor preenche áreas transparentes da seção capturada com
      // o fundo do tema (em dark mode, escuro em vez de branco).
      const canvas = await captureWithExpand(el, {
        backgroundColor: tema.background,
        larguraDeTela: larguraNaTela.get(el),
        ...(scale ? { scale } : {}),
      });
      canvases.push(canvas);
    }
    if (canvases.length === 0) {
      throw new Error("capturarCanvasCombinado: nenhum elemento válido para capturar.");
    }
    const subtitle = `${marcaDaExportacao()}  •  ${new Date().toLocaleString("pt-BR")}`;
    const combined = combineCanvasesVertical(canvases, {
      padding,
      title: titulo,
      subtitle,
      backgroundColor: tema.background,
      titleColor: tema.textPrimary,
      subtitleColor: tema.textSecondary,
      accentColor: tema.accent,
      fontFamily: tema.fontFamily,
    });
    // Antes era só um console.warn e o arquivo em branco era gravado assim
    // mesmo. Falhar aqui é melhor do que entregar uma imagem vazia.
    const inviavel = motivoImagemInviavel(combined);
    if (inviavel) {
      throw new Error(inviavel);
    }
    return combined;
  } finally {
    if (previousBodyAttr) {
      document.body.setAttribute("data-export-mode", previousBodyAttr);
    } else {
      document.body.removeAttribute("data-export-mode");
    }
    if (expandHooks.afterCapture) await expandHooks.afterCapture();
  }
}

/**
 * @param {Object} opts
 * @param {string} opts.titulo
 * @param {HTMLElement[]} opts.elements
 * @param {Object} [opts.expandHooks]
 * @param {("png"|"jpg")} [opts.formato="png"]
 * @param {number} [opts.padding=40]
 * @returns {Promise<{ filename, dataUrl, width, height }>}
 */
export async function salvarImagem(opts) {
  const { titulo = "Exportação", formato = "png" } = opts;
  const combined = await capturarCanvasCombinado(opts);
  const mime = formato === "jpg" ? "image/jpeg" : "image/png";
  const dataUrl =
    formato === "jpg" ? combined.toDataURL(mime, 0.92) : combined.toDataURL(mime);
  const ext = formato === "jpg" ? "jpg" : "png";
  const filename = `${slugifyFilename(titulo)}_${timestampForFilename()}.${ext}`;
  downloadDataUrl(dataUrl, filename);
  return { filename, dataUrl, width: combined.width, height: combined.height };
}

/**
 * Gera Blob da imagem combinada — sem download. Usado pelo executor de
 * agendamento.
 *
 * @returns {Promise<{ blob, filename, width, height }>}
 */
export async function gerarBlobImagem(opts) {
  const { titulo = "Exportação", formato = "png" } = opts;
  const combined = await capturarCanvasCombinado(opts);
  const mime = formato === "jpg" ? "image/jpeg" : "image/png";
  const quality = formato === "jpg" ? 0.92 : undefined;
  const blob = await canvasToBlob(combined, mime, quality);
  const ext = formato === "jpg" ? "jpg" : "png";
  const filename = `${slugifyFilename(titulo)}_${timestampForFilename()}.${ext}`;
  return { blob, filename, width: combined.width, height: combined.height };
}
